import React, { useState } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, today, uuid, notify, staffUserIds, syncSchedulePayment, reconcileInstallmentSchedule } from '../../core/helpers';
import { sb } from '../../core/db';
import { KRAYA } from '../../core/constants';
import { Icon, InlineIcon } from '../../components/Icon';


function RecordPaymentModal({ data, onClose, invoices, setInvoices, payments, setPayments, clients, setClients, showToast }) {
  const { invoiceId } = data;
  const { users, runSubmitting, user } = useApp();
  const invoice = invoices.find(i => i.id === invoiceId);
  const client  = clients.find(c => c.id === invoice?.clientId);
  const outstanding = invoice ? Math.max(0, (invoice.total || 0) - (invoice.paid || 0)) : 0;

  const [form, setForm] = useState({
    amount: outstanding,
    mode: "bank_transfer",
    reference: "",
    date: new Date().toISOString().split("T")[0],
    notes: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const syncClientBilling = (clientId, paidDelta) => {
    if (!clientId || !paidDelta) return;
    setClients?.(cs => cs.map(c => {
      if (c.id !== clientId) return c;
      const collected = (c.collected || 0) + paidDelta;
      const pending = Math.max(0, (c.totalBilling || 0) - collected);
      return { ...c, collected, pending };
    }));
  };

  const save = async () => {
    if (!invoice) { showToast("Invoice not found — refresh and try again", "error"); return; }
    if (!form.amount || Number(form.amount) <= 0) { showToast("Enter valid amount", "error"); return; }
    if (Number(form.amount) > outstanding + 0.01) {
      showToast(`Amount cannot exceed outstanding balance of ${INR(outstanding)}`, "error");
      return;
    }

    await runSubmitting('Recording payment…', async () => {
      try {
        const paidAmt = Number(form.amount);
        const newPaid = (invoice.paid || 0) + paidAmt;
        const newPending = Math.max(0, (invoice.total || 0) - newPaid);
        const newStatus = newPending <= 0 ? "paid" : "partial";
        const paymentId = uuid();

        await sb.update("invoices", "id", invoiceId, {
          paid: newPaid,
          pending: newPending,
          status: newStatus,
        });

        await sb.insert("payments", {
          id: paymentId,
          invoice_id: invoiceId,
          company_id: invoice.clientId,
          client_name: invoice.clientName,
          amount: paidAmt,
          mode: form.mode,
          reference: form.reference || "",
          date: form.date,
          notes: form.notes || "",
          status: "approved",
          recorded_by: user?.id || null,
        });

        try {
          await syncSchedulePayment(sb, invoiceId, paidAmt);
          await reconcileInstallmentSchedule(sb, invoiceId, { ...invoice, paid: newPaid, pending: newPending, status: newStatus }, [
            ...payments,
            { id: paymentId, invoiceId, amount: paidAmt, status: "approved" },
          ]);
        } catch { /* best-effort instalment sync */ }

        setInvoices(is => is.map(i => i.id === invoiceId
          ? { ...i, paid: newPaid, pending: newPending, status: newStatus }
          : i));
        setPayments(ps => [...ps, {
          id: paymentId,
          invoiceId,
          clientId: invoice.clientId,
          clientName: invoice.clientName,
          amount: paidAmt,
          mode: form.mode,
          reference: form.reference,
          date: form.date,
          notes: form.notes,
          status: "approved",
          recordedBy: user?.id,
        }]);
        syncClientBilling(invoice.clientId, paidAmt);

        KRAYA.paymentReceived(client?.phone || "", invoice.clientName, INR(paidAmt), invoice.invoiceNo);
        const teamIds = [...new Set([client?.assignedTo, ...staffUserIds(users)].filter(Boolean))];
        if (teamIds.length) {
          notify(teamIds, {
            type: "payment",
            title: "Payment recorded",
            body: `${INR(paidAmt)} recorded for ${invoice.invoiceNo} (${invoice.clientName}).`,
            meta: { invoiceId, paymentId, modal: "view-invoice" },
          });
        }
        showToast(`Payment of ${INR(paidAmt)} recorded for ${invoice.invoiceNo}`, "success");
        onClose();
      } catch (e) {
        showToast(e.message || "Could not record payment", "error");
        throw e;
      }
    }, 'payment');
  };

  const MODES = [
    { id: "bank_transfer", label: "Bank Transfer / NEFT" },
    { id: "upi",           label: "UPI" },
    { id: "cheque",        label: "Cheque" },
    { id: "cash",          label: "Cash" },
  ];

  return (
    <div className="modal-box" style={{ maxWidth: 480 }}>
      <div className="modal-head"><div className="modal-title">Record Payment</div><button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button></div>
      <div className="modal-body">
        {invoice && (
          <div style={{ background: "linear-gradient(135deg,var(--navy),#1E40AF)", borderRadius: 10, padding: "14px 18px", marginBottom: 20, color: "#fff" }}>
            <div style={{ fontSize: 12, opacity: .7, marginBottom: 4 }}>{invoice.invoiceNo} · {invoice.clientName}</div>
            <div style={{ display: "flex", gap: 20 }}>
              <div><div style={{ fontSize: 10, opacity: .6 }}>Total</div><div style={{ fontSize: 16, fontWeight: 700 }}>{INR(invoice.total)}</div></div>
              <div><div style={{ fontSize: 10, opacity: .6 }}>Paid so far</div><div style={{ fontSize: 16, fontWeight: 700, color: "#86EFAC" }}>{INR(invoice.paid)}</div></div>
              <div><div style={{ fontSize: 10, opacity: .6 }}>Outstanding</div><div style={{ fontSize: 16, fontWeight: 700, color: "#FCA5A5" }}>{INR(outstanding)}</div></div>
            </div>
          </div>
        )}
        {!invoice && (
          <div style={{ padding: 12, background: "#FEF2F2", borderRadius: 8, fontSize: 13, color: "var(--red)", marginBottom: 16 }}>
            Invoice not found. Close and open again from the invoices list.
          </div>
        )}
        <div className="form-grid">
          <div className="f-group">
            <label className="f-label">Payment Amount (₹) <span className="f-req">*</span></label>
            <input className="f-input" type="number" value={form.amount} onChange={e => set("amount", e.target.value)} max={outstanding} />
            <div className="f-hint">Maximum: {INR(outstanding)}</div>
          </div>
          <div className="f-group">
            <label className="f-label">Payment Mode</label>
            <select className="f-select" value={form.mode} onChange={e => set("mode", e.target.value)}>
              {MODES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">Reference / UTR Number</label>
            <input className="f-input" placeholder="NEFT/UPI/Cheque reference" value={form.reference} onChange={e => set("reference", e.target.value)} />
          </div>
          <div className="f-group">
            <label className="f-label">Payment Date</label>
            <input className="f-input" type="date" value={form.date} onChange={e => set("date", e.target.value)} />
          </div>
          <div className="f-group">
            <label className="f-label">Notes</label>
            <textarea className="f-textarea" rows={2} placeholder="Optional notes…" value={form.notes} onChange={e => set("notes", e.target.value)} />
          </div>
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-green" onClick={save} disabled={!invoice}><InlineIcon name="money" size={14} gap={5}>Record Payment</InlineIcon></button>
      </div>
    </div>
  );
}

export { RecordPaymentModal };
